import { inject, Injectable, REQUEST } from '@angular/core';
import { EnvironmentService } from '../environment/environment.service';
import { FILE_SCHEMES, joinBaseUrl, NAVIGATION_SCHEMES } from '../../utils/resolve-url.util';

/**
 * The host a (possibly relative) URL should be resolved against:
 * - `frontend`: the Angular app origin (`window.location`) — for navigation to app routes.
 * - `media`: the media host (`FRONTEND_URL || BACKEND_URL`) — for CMS images and attachments.
 * - `backend`: the backend host (`BACKEND_URL`) — for files served straight from the CMS/backend
 *   (transactional documents like invoices and order/debit documents).
 */
export type UrlBase = 'frontend' | 'media' | 'backend';

/**
 * Central resolver that turns a (possibly relative) URL into an absolute one against the host
 * appropriate for the given intent. It is the single entry point all download/link/media code
 * delegates to, so the base-URL choice lives in one place instead of being re-implemented per call site.
 */
@Injectable({
  providedIn: 'root'
})
export class UrlResolverService {
  // Services
  private readonly envService = inject(EnvironmentService);
  /** The incoming request while rendering on the server, null in the browser */
  private readonly request = inject(REQUEST, { optional: true });

  // Properties & Signals
  /**
   * The frontend app origin (protocol + host): `window.location` in the browser, the origin of the
   * incoming request on the server (protocol from the proxy's `x-forwarded-proto`, host from the `Host` header,
   * checked against `ALLOWED_HOSTS`). Without it the
   * server rendered relative navigation links as plain elements and the browser as `<a>`, so the SSR
   * HTML missed those links and the hydration rebuilt them. `''` when neither is available.
   */
  private readonly frontendOrigin =
    typeof window !== 'undefined' && window.location
      ? `${window.location.protocol}//${window.location.host}`
      : this.request
        ? new URL(this.request.url).origin
        : '';

  /** The backend host (`BACKEND_URL`) for transactional documents. `''` when not configured. */
  private readonly backendOrigin = this.envService.env.BACKEND_URL?.trim() || '';

  /**
   * The media host: prefers `FRONTEND_URL` (the media-serving frontend) and falls back to the backend
   * origin. `''` when neither is configured.
   */
  private readonly mediaOrigin = this.envService.env.FRONTEND_URL?.trim() || this.backendOrigin;

  // Methods
  /**
   * Resolves a URL against the origin that matches the given intent.
   * @param url the raw URL, absolute or relative
   * @param base which host a relative path belongs to
   * @returns the absolute URL, or undefined for empty input / when the chosen base is not configured
   */
  resolveUrl(url: string | undefined | null, base: UrlBase): string | undefined {
    if (base === 'frontend') {
      return joinBaseUrl(url, this.frontendOrigin, NAVIGATION_SCHEMES);
    }
    return joinBaseUrl(url, base === 'media' ? this.mediaOrigin : this.backendOrigin, FILE_SCHEMES);
  }
}

